// src/security/features.catalog.ts
import { PRIVATE_ROUTE_CATALOG, type RouteCategory } from "./routes.catalog";

export type FeatureNode = {
    /** ID estable para gating por suscripción (no cambiar una vez publicado) */
    id: string;
    /** Etiqueta para UI de configuración de planes */
    label: string;
    /** Categoría funcional (para agrupar en UI) */
    category: RouteCategory;
    /** Descripción opcional para la UI de planes */
    description?: string;
};

/**
 * Mapa featureId -> permitido, guardado como JSON en SuscriptionType.features.
 * null/undefined significa "plan sin restricciones" (backwards-compat).
 */
export type PlanFeatures = Record<string, boolean> | null;

/**
 * Catálogo de acciones granulares (botones/funciones) controlables por plan.
 * Usa el namespace "action:" para no colisionar con los ids de rutas del ACL.
 * Para agregar un nuevo permiso granular: registrar aquí el id estable y
 * envolver la acción con guardFeature/hasFeature de useFeatureAccess.
 */
export const ACTION_FEATURE_CATALOG: FeatureNode[] = [
    {
        id: "action:exemption:apply",
        label: "Exoneraciones en documentos",
        category: "invoicing",
        description: "Agregar exoneraciones a facturas y cotizaciones",
    },
    {
        id: "action:pricemanaged:change",
        label: "Cambiar tipo de manejo de precios",
        category: "configuration",
        description: "Permite al tenant cambiar el tipo de manejo de precios en su perfil",
    },
];

/**
 * Catálogo completo que alimenta los checkboxes de la pantalla de planes:
 * todas las rutas privadas (mismos ids estables del ACL) + acciones granulares.
 */
export const PLAN_FEATURE_CATALOG: FeatureNode[] = [
    ...PRIVATE_ROUTE_CATALOG.map(({ id, label, category }) => ({ id, label, category })),
    ...ACTION_FEATURE_CATALOG,
];

/**
 * Semántica default-allow (backwards-compat):
 * - features null/undefined => plan sin restricciones, todo permitido.
 * - key ausente en el JSON => permitido (bloquear requiere false explícito),
 *   así los features nuevos no rompen planes ya guardados.
 */
export function isFeatureAllowed(features: PlanFeatures | undefined, featureId: string): boolean {
    if (features == null) return true;
    return features[featureId] !== false;
}

/** Parsea el campo features del plan (string JSON u objeto) de forma tolerante. */
export function parsePlanFeatures(raw: unknown): PlanFeatures {
    if (raw == null) return null;
    if (typeof raw === "object") return raw as Record<string, boolean>;
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return typeof parsed === "object" && parsed !== null ? parsed : null;
        } catch {
            return null;
        }
    }
    return null;
}
